/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 */

import { axiosInstance } from '@coze-arch/bot-http';

export interface OpenCodeSessionItem {
  id: number;
  opencode_session_id: string;
  title: string;
  created_at: number;
  updated_at: number;
}

interface ApiWrap<T> {
  code: number;
  msg: string;
  data: T;
}

function unwrapApiData<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') {
    return undefined;
  }
  if ('code' in res && 'data' in res && (res as ApiWrap<T>).code === 0) {
    return (res as ApiWrap<T>).data;
  }
  const axiosBody = (res as { data?: ApiWrap<T> }).data;
  if (axiosBody && axiosBody.code === 0) {
    return axiosBody.data;
  }
  return undefined;
}

export async function listOpenCodeSessions(): Promise<OpenCodeSessionItem[]> {
  const res = await axiosInstance.get('/api/opencode/sessions');
  const payload = unwrapApiData<{ items: OpenCodeSessionItem[] }>(res);
  return Array.isArray(payload?.items) ? payload.items : [];
}

export async function createOpenCodeSession(title?: string): Promise<{
  item: OpenCodeSessionItem;
  embed_url: string;
}> {
  const res = await axiosInstance.post('/api/opencode/sessions', { title });
  const payload = unwrapApiData<OpenCodeSessionItem & { embed_url: string }>(
    res,
  );
  if (!payload) {
    throw new Error('create session failed');
  }
  const { embed_url, ...item } = payload;
  return { item, embed_url };
}

export async function deleteOpenCodeSession(id: number): Promise<void> {
  await axiosInstance.delete(`/api/opencode/sessions/${id}`);
}

export async function updateOpenCodeSession(
  id: number,
  title: string,
): Promise<OpenCodeSessionItem> {
  const res = await axiosInstance.patch(`/api/opencode/sessions/${id}`, {
    title,
  });
  const payload = unwrapApiData<OpenCodeSessionItem>(res);
  if (!payload) {
    throw new Error('update session failed');
  }
  return payload;
}

export async function getOpenCodeSessionEmbed(id: number): Promise<string> {
  const res = await axiosInstance.get(`/api/opencode/sessions/${id}/embed`);
  const payload = unwrapApiData<{ embed_url: string }>(res);
  return payload?.embed_url ?? '';
}
